import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  getAllVideos, 
  addVideo, 
  updateVideo, 
  deleteVideo,
  extractYouTubeId,
  extractVimeoId,
  getYouTubeThumbnail,
  getVimeoThumbnail,
  getYouTubeVideoInfo
} from '../services/videoService';
import { 
  getAllVideoEngagements, 
  isVideoTrending 
} from '../services/videoEngagementService';
import { PlayCircle, Pause, RefreshCw, Search, Zap } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { useEntityContext } from '../hooks/useEntityContext';
import { isPhoneViewport } from '../utils/mobileViewport';

const SuperKonnectedCarousel = ({ isAdmin = false, filterMode = 'all', regionFilter = 'all', searchQuery = '', displaySortOrder = 'relevance' }) => {
  // State management
  const [isPlaying, setIsPlaying] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [currentTransform, setCurrentTransform] = useState(0);
  const [animationSpeed, setAnimationSpeed] = useState(40); // seconds for full cycle
  const [currentFilter, setCurrentFilter] = useState('all');
  const [currentSort, setCurrentSort] = useState('relevance');
  const [searchTerm, setSearchTerm] = useState('');
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showBrowseModal, setShowBrowseModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isScrollbarDragging, setIsScrollbarDragging] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [adminActiveTab, setAdminActiveTab] = useState('upload');
  const [editingVideo, setEditingVideo] = useState(null);
  const [uploadType, setUploadType] = useState('file'); // 'file' or 'link'
  const [videoLink, setVideoLink] = useState('');
  const [linkPreview, setLinkPreview] = useState(null);
  const [availableBadges, setAvailableBadges] = useState([
    '🎙️ PODCAST',
    '👋 ONTMOET Frank',
    '🔥 TRENDING',
    '👑 BEST BEKEKEN',
    '⭐ FEATURED'
  ]);
  const [selectedBadge, setSelectedBadge] = useState('');
  const [customBadgeText, setCustomBadgeText] = useState('');
  const [videoFormData, setVideoFormData] = useState({
    title: '',
    description: '',
    type: 'podcast',
    guest: '',
    badge: ''
  });
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest', 'oldest', 'title', 'views', 'shares'
  const [showAnalytics, setShowAnalytics] = useState(true);
  const [firebaseVideos, setFirebaseVideos] = useState([]);
  const [firebaseLoading, setFirebaseLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userRegions, setUserRegions] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [uploaderProfile, setUploaderProfile] = useState(null);
  const [guestProfile, setGuestProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [sendingConnectionRequest, setSendingConnectionRequest] = useState(false);
  const [showGuidedVideoOverlay, setShowGuidedVideoOverlay] = useState(false);
  const [collaborationVideoIds, setCollaborationVideoIds] = useState(new Set());

  // Refs
  const trackRef = useRef(null);
  const containerRef = useRef(null);
  const browseGridRef = useRef(null);

  // Load user's regions and entity context
  const auth = getAuth();
  const currentUser = auth.currentUser;
  const { activeEntityId, entities } = useEntityContext(currentUser?.uid);
  const activeEntity = entities.find(e => e.id === activeEntityId);

  useEffect(() => {
    const loadUserRegions = async () => {
      if (!currentUser) return;

      try {
        const db = getFirestore();
        const userDoc = await getDoc(doc(db, 'userProfiles', currentUser.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserRegions(data.selectedRegions || []);
          console.log('📍 User regions loaded:', data.selectedRegions || []);
        }
      } catch (error) {
        console.error('Error loading user regions:', error);
      }
    };

    loadUserRegions();
  }, [currentUser]);

  // Load videos from Firebase
  useEffect(() => {
    const loadVideos = async () => {
      try {
        setFirebaseLoading(true);
        const videos = await getAllVideos();
        setFirebaseVideos(videos);
        console.log('Loaded videos from Firebase:', videos);
      } catch (error) {
        console.error('Error loading videos:', error);
        setError('Failed to load videos');
      } finally {
        setFirebaseLoading(false);
      }
    };

    loadVideos();
  }, []);

  // Lock body scroll when guided video overlay is open
  useEffect(() => {
    if (showGuidedVideoOverlay) {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Restore scroll position
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [showGuidedVideoOverlay]);

  // ESC key handler for guided video overlay
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && showGuidedVideoOverlay) {
        setShowGuidedVideoOverlay(false);
      }
    };
    
    if (showGuidedVideoOverlay) {
      window.addEventListener('keydown', handleEscKey);
      return () => window.removeEventListener('keydown', handleEscKey);
    }
  }, [showGuidedVideoOverlay]);

  // Load user profiles when video is selected
  useEffect(() => {
    const loadProfiles = async () => {
      if (!selectedVideo) {
        setUploaderProfile(null);
        setGuestProfile(null);
        return;
      }

      setLoadingProfile(true);
      const db = getFirestore();
      const profiles = {};

      try {
        // Load uploader profile if userId exists
        if (selectedVideo.userId) {
          try {
            const uploaderDoc = await getDoc(doc(db, 'userProfiles', selectedVideo.userId));
            if (uploaderDoc.exists()) {
              profiles.uploader = { id: selectedVideo.userId, ...uploaderDoc.data() };
            }
          } catch (error) {
            console.error('Error loading uploader profile:', error);
          }
        }

        // Load guest profile if guest name exists (search by displayName)
        if (selectedVideo.guest) {
          try {
            const { collection, query, where, getDocs } = await import('firebase/firestore');
            const guestQuery = query(
              collection(db, 'userProfiles'),
              where('displayName', '==', selectedVideo.guest)
            );
            const guestSnapshot = await getDocs(guestQuery);
            if (!guestSnapshot.empty) {
              const guestDoc = guestSnapshot.docs[0];
              profiles.guest = { id: guestDoc.id, ...guestDoc.data() };
            }
          } catch (error) {
            console.error('Error loading guest profile:', error);
          }
        }

        setUploaderProfile(profiles.uploader || null);
        setGuestProfile(profiles.guest || null);
      } catch (error) {
        console.error('Error loading profiles:', error);
      } finally {
        setLoadingProfile(false);
      }
    };

    loadProfiles();
  }, [selectedVideo]);
  const scrollThumbRef = useRef(null);
  const dragStartX = useRef(0);
  const dragStartTransform = useRef(0);
  const scrollDragStart = useRef({ y: 0, scrollTop: 0 });

  const gradients = {
    gradient1: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    gradient2: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    gradient3: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    gradient4: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
    gradient5: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    gradient6: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
    gradient7: "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
    gradient8: "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)"
  };

  // Video link validation and preview functions
  const extractVideoId = (url) => {
    const patterns = {
      youtube: /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/,
      vimeo: /vimeo\.com\/(?:.*\/)?(\d+)/
    };
    
    const youtubeMatch = url.match(patterns.youtube);
    if (youtubeMatch) return { type: 'youtube', id: youtubeMatch[1] };
    
    const vimeoMatch = url.match(patterns.vimeo);
    if (vimeoMatch) return { type: 'vimeo', id: vimeoMatch[1] };
    
    return null;
  };

  const generateThumbnail = (videoInfo) => {
    if (videoInfo.type === 'youtube') {
      return `https://img.youtube.com/vi/${videoInfo.id}/maxresdefault.jpg`;
    } else if (videoInfo.type === 'vimeo') {
      // Vimeo requires API call for thumbnail, using placeholder for now
      return `https://via.placeholder.com/320x180/FF6B35/FFFFFF?text=Vimeo+Video`;
    }
    return null;
  };

  const handleLinkChange = async (link) => {
    setVideoLink(link);
    
    if (link.includes('youtube.com') || link.includes('youtu.be')) {
      const videoId = extractYouTubeId(link);
      if (videoId) {
        const thumbnail = getYouTubeThumbnail(videoId);
        
        // 🎯 AUTO-FILL: Fetch real video info from YouTube API
        const YOUTUBE_API_KEY = 'AIzaSyBOti4mM-6x9WJnVgxVLfRBhVsrCH0uL68';
        const videoInfo = await getYouTubeVideoInfo(videoId, YOUTUBE_API_KEY);
        
        if (videoInfo) {
          // ✅ Auto-fill form with video info
          setVideoFormData(prev => ({
            ...prev,
            title: videoInfo.title,
            description: videoInfo.description.substring(0, 500) // Limit description length
          }));
          
          setLinkPreview({
            type: 'youtube',
            id: videoId,
            url: link,
            thumbnail: videoInfo.thumbnail || thumbnail,
            title: videoInfo.title,
            description: videoInfo.description.substring(0, 200) + '...',
            duration: videoInfo.duration
          });
          
          console.log('✅ Video info auto-filled:', videoInfo.title);
        } else {
          // Fallback if API fails
          setLinkPreview({
            type: 'youtube',
            id: videoId,
            url: link,
            thumbnail: thumbnail,
            title: 'YouTube Video',
            description: 'Video van YouTube'
          });
          console.warn('⚠️ Could not fetch video info, using fallback');
        }
      }
    } else if (link.includes('vimeo.com')) {
      const videoId = extractVimeoId(link);
      if (videoId) {
        try {
          const thumbnail = await getVimeoThumbnail(videoId);
          setLinkPreview({
            type: 'vimeo',
            id: videoId,
            url: link,
            thumbnail: thumbnail,
            title: 'Vimeo Video',
            description: 'Video van Vimeo'
          });
        } catch (error) {
          console.error('Error getting Vimeo thumbnail:', error);
          setLinkPreview({
            type: 'vimeo',
            id: videoId,
            url: link,
            thumbnail: '',
            title: 'Vimeo Video',
            description: 'Video van Vimeo'
          });
        }
      }
    } else {
      setLinkPreview(null);
    }
  };

  // Badge management functions
  const addCustomBadge = () => {
    const trimmedText = customBadgeText.trim();
    if (!trimmedText) {
      alert('Voer een badge tekst in');
      return;
    }
    
    if (availableBadges.includes(trimmedText)) {
      alert('Deze badge bestaat al');
      return;
    }
    
    setAvailableBadges([...availableBadges, trimmedText]);
    setSelectedBadge(trimmedText);
    setCustomBadgeText('');
    alert(`Badge "${trimmedText}" succesvol toegevoegd!`);
  };

  const removeBadge = (badgeToRemove) => {
    setAvailableBadges(availableBadges.filter(badge => badge !== badgeToRemove));
    if (selectedBadge === badgeToRemove) {
      setSelectedBadge('');
    }
  };

  // Video management functions
  const handleEditVideo = (video) => {
    setEditingVideo(video);
    setVideoFormData({
      title: video.title,
      description: video.description,
      type: video.type,
      guest: video.guest || '',
      badge: ''
    });
    
    // Set the selected badge based on video properties
    let videoBadge = '';
    if (video.mostViewed) {
      videoBadge = '👑 BEST BEKEKEN';
    } else if (video.trending) {
      videoBadge = '🔥 TRENDING';
    } else if (video.guest) {
      videoBadge = `👋 ONTMOET ${video.guest.split(' ')[0]}`;
    } else if (video.type === 'podcast') {
      videoBadge = '🎙️ PODCAST';
    } else if ((video.todayShares || 0) > 50) {
      videoBadge = `📤 ${video.todayShares || 0} shares`;
    }
    
    setSelectedBadge(videoBadge);
    setAdminActiveTab('upload');
  };

  const handleDeleteVideo = async (videoId) => {
    if (window.confirm('Weet je zeker dat je deze video wilt verwijderen?')) {
      try {
        setFirebaseLoading(true);
        await deleteVideo(videoId);
        console.log('Video verwijderd uit Firebase:', videoId);
        
        // Reload videos from Firebase
        const updatedVideos = await getAllVideos();
        setFirebaseVideos(updatedVideos);
        setError(null); // Clear any previous errors
        
        // Force re-render of carousel
        console.log('Video deleted, reloading carousel with', updatedVideos.length, 'videos');
        
        alert('Video succesvol verwijderd!');
      } catch (error) {
        console.error('Error deleting video:', error);
        alert('Er is een fout opgetreden bij het verwijderen van de video');
      } finally {
        setFirebaseLoading(false);
      }
    }
  };

  const handleSaveVideo = async () => {
    if (!videoFormData.title.trim()) {
      alert('Voer een video titel in');
      return;
    }
    
    try {
      setFirebaseLoading(true);
      
      // Get current user
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      // Get user's regions for automatic assignment
      let uploaderRegions = [];
      if (currentUser) {
        try {
          const db = getFirestore();
          const userDoc = await getDoc(doc(db, 'userProfiles', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            uploaderRegions = userData.selectedRegions || [];
          }
        } catch (error) {
          console.error('Error loading uploader regions:', error);
        }
      }
      
      // Check if user is uploading as an entity (club/mastermind/community)
      const isEntityUpload = activeEntity && 
        (activeEntity.type === 'networkclub' || activeEntity.type === 'mastermind' || activeEntity.type === 'community') &&
        (activeEntity.role === 'owner' || activeEntity.role === 'organizer' || activeEntity.role === 'board');
      
      // Get clubId if it's a networkclub
      let clubId = undefined;
      if (activeEntity?.type === 'networkclub' && activeEntityId) {
        try {
          const db = getFirestore();
          const entityDoc = await getDoc(doc(db, 'entities', activeEntityId));
          if (entityDoc.exists()) {
            const entityData = entityDoc.data();
            clubId = entityData.linkedNetworkClubId;
          }
        } catch (error) {
          console.error('Error fetching clubId:', error);
        }
      }

      const newVideoData = {
        title: videoFormData.title,
        description: videoFormData.description,
        type: videoFormData.type,
        guest: videoFormData.guest,
        badge: selectedBadge,
        views: editingVideo ? editingVideo.views : 0,
        flames: editingVideo ? editingVideo.flames : 0,
        publishDate: editingVideo ? editingVideo.publishDate : new Date(),
        thumbnail: editingVideo ? editingVideo.thumbnail : 'gradient1',
        // Set badge-related properties based on selected badge
        mostViewed: selectedBadge === '👑 BEST BEKEKEN',
        trending: selectedBadge === '🔥 TRENDING',
        // Handle video link if provided
        videoUrl: linkPreview?.url || '',
        platform: linkPreview?.type || 'upload',
        platformId: linkPreview?.id || '',
        thumbnailUrl: linkPreview?.thumbnail || '',
        // Store uploader info for region fallback
        userId: currentUser?.uid || null,
        uploaderRegions: uploaderRegions.length > 0 ? uploaderRegions : undefined,
        // ✅ NIEUW: Entity linking voor clubs/masterminds/communities
        entityId: isEntityUpload ? activeEntityId : undefined,
        entityType: isEntityUpload ? activeEntity.type : undefined,
        clubName: isEntityUpload ? activeEntity.name : undefined,
        clubId: isEntityUpload ? clubId : undefined,
        uploadedByEntity: isEntityUpload || false
      };

      if (editingVideo) {
        // Update existing video in Firebase
        await updateVideo(editingVideo.id, newVideoData);
        console.log('Video bijgewerkt in Firebase:', newVideoData);
      } else {
        // Add new video to Firebase
        const videoId = await addVideo(newVideoData);
        console.log('Nieuwe video toegevoegd aan Firebase:', videoId);
      }
      
      // Reload videos from Firebase
      const updatedVideos = await getAllVideos();
      setFirebaseVideos(updatedVideos);
      setError(null); // Clear any previous errors
      
      // Force re-render of carousel
      console.log('Video saved, reloading carousel with', updatedVideos.length, 'videos');
      
      alert(editingVideo ? 'Video succesvol bijgewerkt!' : 'Video succesvol toegevoegd!');
      
      // Reset form
      setVideoFormData({
        title: '',
        description: '',
        type: 'podcast',
        guest: '',
        badge: ''
      });
      setSelectedBadge('');
      setEditingVideo(null);
      setVideoLink('');
      setLinkPreview(null);
      setAdminActiveTab('manage');
      
    } catch (error) {
      console.error('Error saving video:', error);
      alert('Er is een fout opgetreden bij het opslaan van de video');
    } finally {
      setFirebaseLoading(false);
    }
  };

  // State for video management search and filter
  const [videoManagementSearch, setVideoManagementSearch] = useState('');
  const [videoManagementTypeFilter, setVideoManagementTypeFilter] = useState('all');

  // Sorting function - use ONLY Firebase videos (no mock data fallback)
  // Also applies search and type filter for video management
  const getSortedVideos = () => {
    if (firebaseVideos.length === 0) return [];
    
    // Start with all Firebase videos
    let filtered = [...firebaseVideos];
    
    // Apply type filter (for video management)
    if (videoManagementTypeFilter !== 'all') {
      filtered = filtered.filter(video => {
        if (videoManagementTypeFilter === 'podcast') return video.type === 'podcast';
        if (videoManagementTypeFilter === 'video') return video.type === 'video';
        return true;
      });
    }
    
    // Apply search filter (for video management)
    if (videoManagementSearch.trim()) {
      const searchLower = videoManagementSearch.toLowerCase();
      filtered = filtered.filter(video => 
        video.title?.toLowerCase().includes(searchLower) ||
        video.description?.toLowerCase().includes(searchLower) ||
        video.guest?.toLowerCase().includes(searchLower) ||
        video.clubName?.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply sorting
    const sorted = [...filtered];
    switch(sortOrder) {
      case 'newest':
        return sorted.sort((a, b) => {
          const dateA = a.publishDate?.toDate ? a.publishDate.toDate() : new Date(a.publishDate);
          const dateB = b.publishDate?.toDate ? b.publishDate.toDate() : new Date(b.publishDate);
          return dateB - dateA;
        });
      case 'oldest':
        return sorted.sort((a, b) => {
          const dateA = a.publishDate?.toDate ? a.publishDate.toDate() : new Date(a.publishDate);
          const dateB = b.publishDate?.toDate ? b.publishDate.toDate() : new Date(b.publishDate);
          return dateA - dateB;
        });
      case 'title':
        return sorted.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      case 'views':
        return sorted.sort((a, b) => (b.views || 0) - (a.views || 0));
      case 'shares':
        return sorted.sort((a, b) => (b.todayShares || 0) - (a.todayShares || 0));
      default:
        return sorted;
    }
  };

  // Analytics calculation - use ONLY Firebase videos (no mock data fallback)
  const getAnalytics = () => {
    if (firebaseVideos.length === 0) {
      return {
        totalViews: 0,
        totalShares: 0,
        totalVideos: 0,
        podcastCount: 0,
        videoCount: 0,
        trendingCount: 0
      };
    }
    const totalViews = firebaseVideos.reduce((sum, video) => sum + (video.views || 0), 0);
    // Calculate total shares from videoEngagements (today's shares) or fallback to 0
    const totalShares = Array.from(videoEngagements.values()).reduce((sum, engagement) => sum + (engagement.shares || 0), 0);
    const totalVideos = firebaseVideos.length;
    const podcastCount = firebaseVideos.filter(v => v.type === 'podcast').length;
    const videoCount = firebaseVideos.filter(v => v.type === 'video').length;
    const trendingCount = firebaseVideos.filter(v => v.trending).length;
    
    return {
      totalViews,
      totalShares,
      totalVideos,
      podcastCount,
      videoCount,
      trendingCount
    };
  };

  // State for video engagements
  const [videoEngagements, setVideoEngagements] = useState(new Map());
  const [engagementLoading, setEngagementLoading] = useState(false);

  // Load today's engagement for all videos
  useEffect(() => {
    const loadEngagements = async () => {
      if (firebaseVideos.length === 0) return;
      
      // Filter valid videos
      const validVideos = firebaseVideos.filter(v => v && v.title && v.id);
      if (validVideos.length === 0) return;
      
      setEngagementLoading(true);
      try {
        const engagements = await getAllVideoEngagements(validVideos);
        setVideoEngagements(engagements);
        console.log('📊 Video engagements loaded:', Array.from(engagements.values()));
      } catch (error) {
        console.error('Error loading video engagements:', error);
      } finally {
        setEngagementLoading(false);
      }
    };

    // Load engagements when videos change, and refresh every 5 minutes
    loadEngagements();
    const interval = setInterval(loadEngagements, 5 * 60 * 1000); // 5 minutes
    
    return () => clearInterval(interval);
  }, [firebaseVideos.length]);

  // Update video data with real-time trending based on today's engagement
  const updateTrendingStatus = (videos) => {
    if (videoEngagements.size === 0) {
      // If no engagement data yet, return videos as-is
      return videos;
    }

    const allEngagements = Array.from(videoEngagements.values());
    
    return videos.map(video => {
      const engagement = videoEngagements.get(video.id);
      if (!engagement) {
        return { ...video, trending: false };
      }
      
      const isTrending = isVideoTrending(engagement, allEngagements);
      return {
        ...video,
        trending: isTrending,
        // Update engagement metrics for display
        todayEngagement: engagement.engagementScore,
        todayReactions: engagement.reactions,
        todayComments: engagement.comments,
        todayShares: engagement.shares
      };
    });
  };

  // Helper functions
  const formatDate = (date) => {
    if (!date) return 'Onbekend';
    
    // Handle Firebase Timestamp
    let dateObj;
    if (date.toDate && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    } else if (date instanceof Date) {
      dateObj = date;
    } else {
      dateObj = new Date(date);
    }
    
    if (isNaN(dateObj.getTime())) return 'Onbekend';
    
    const now = new Date();
    const diffTime = Math.abs(now - dateObj);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'vandaag';
    if (diffDays === 2) return 'gisteren';
    if (diffDays <= 7) return `${diffDays - 1} dagen geleden`;
    return `${Math.floor(diffDays / 7)} weken geleden`;
  };

  const getVideoTimestamp = (video) => {
    const value = video.publishDate || video.createdAt || video.updatedAt;
    if (!value) return 0;
    const date = value.toDate && typeof value.toDate === 'function' ? value.toDate() : new Date(value);
    const time = date.getTime();
    return Number.isFinite(time) ? time : 0;
  };

  const rankVideos = (videos) => {
    if (displaySortOrder === 'newest') {
      return [...videos].sort((a, b) => getVideoTimestamp(b) - getVideoTimestamp(a));
    }

    return [...videos].sort((a, b) => {
      // 0. Carousel boost (hoogste prioriteit)
      const now = new Date();
      const aBoost = a.carouselBoost?.until?.toDate ? a.carouselBoost.until.toDate() : (a.carouselBoost?.until ? new Date(a.carouselBoost.until) : null);
      const bBoost = b.carouselBoost?.until?.toDate ? b.carouselBoost.until.toDate() : (b.carouselBoost?.until ? new Date(b.carouselBoost.until) : null);
      const aHasActiveBoost = aBoost && aBoost > now && a.carouselBoost?.trending;
      const bHasActiveBoost = bBoost && bBoost > now && b.carouselBoost?.trending;
      
      if (aHasActiveBoost && !bHasActiveBoost) return -1;
      if (!aHasActiveBoost && bHasActiveBoost) return 1;
      
      // 1. Most viewed (blijft)
      if (a.mostViewed && !b.mostViewed) return -1;
      if (!a.mostViewed && b.mostViewed) return 1;
      
      // 2. Trending (blijft, maar nu gebaseerd op engagement of boost)
      const aTrending = a.trending || aHasActiveBoost;
      const bTrending = b.trending || bHasActiveBoost;
      if (aTrending && !bTrending) return -1;
      if (!aTrending && bTrending) return 1;
      
      // 3. NIEUW: Hoge dialogue quality (mutual respect + offspring)
      // Dialogue quality = mutualRespectCount + (profileViewsFromPost * 0.5) + (connectionsFromPost * 2)
      const aDialogue = (a.mutualRespectCount || 0) + 
                        (a.profileViewsFromPost || 0) * 0.5 + 
                        (a.connectionsFromPost || 0) * 2;
      const bDialogue = (b.mutualRespectCount || 0) + 
                        (b.profileViewsFromPost || 0) * 0.5 + 
                        (b.connectionsFromPost || 0) * 2;
      
      // Drempel: 5+ = "actieve dialoog"
      if (aDialogue > 5 && bDialogue <= 5) return -1;
      if (aDialogue <= 5 && bDialogue > 5) return 1;
      if (aDialogue !== bDialogue) return bDialogue - aDialogue;
      
      // 4. Guest videos (blijft)
      if (a.guest && !b.guest) return -1;
      if (!a.guest && b.guest) return 1;
      
      // 5. Fallback: views (zonder flames)
      return (b.views || 0) - (a.views || 0);
    });
  };

  // Carousel drag functionality
  const handleMouseDown = useCallback((e) => {
    if (e.target.closest('.video-card') || e.target.closest('.controls')) {
      return;
    }
    
    e.preventDefault();
    setIsDragging(true);
    dragStartX.current = e.clientX;
    dragStartTransform.current = currentTransform;
  }, [currentTransform]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    
    e.preventDefault();
    const walk = (e.clientX - dragStartX.current) * 1.5;
    const newTransform = dragStartTransform.current + walk;
    
    const track = trackRef.current;
    const container = containerRef.current;
    if (track && container) {
      const maxScroll = -(track.offsetWidth / 2 - container.offsetWidth + 100);
      const clampedTransform = Math.max(Math.min(newTransform, 50), maxScroll);
      setCurrentTransform(clampedTransform);
    }
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      if (isPlaying) {
        setTimeout(() => {
          if (!isDragging) {
            setCurrentTransform(0);
          }
        }, 3000);
      }
    }
  }, [isDragging, isPlaying]);

  // Orange scrollbar functionality
  const updateScrollbar = useCallback(() => {
    const grid = browseGridRef.current;
    const thumb = scrollThumbRef.current;
    
    if (!grid || !thumb) return;
    
    const scrollTop = grid.scrollTop;
    const scrollHeight = grid.scrollHeight;
    const clientHeight = grid.clientHeight;
    
    if (scrollHeight <= clientHeight) {
      thumb.style.display = 'none';
      return;
    }
    
    thumb.style.display = 'block';
    const thumbHeight = Math.max((clientHeight / scrollHeight) * thumb.parentElement.offsetHeight, 50);
    const maxThumbTop = thumb.parentElement.offsetHeight - thumbHeight;
    const thumbTop = (scrollTop / (scrollHeight - clientHeight)) * maxThumbTop;
    
    thumb.style.height = `${thumbHeight}px`;
    thumb.style.top = `${thumbTop}px`;
  }, []);

  const handleScrollbarMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsScrollbarDragging(true);
    scrollDragStart.current = {
      y: e.clientY,
      scrollTop: browseGridRef.current.scrollTop
    };
  }, []);

  const handleScrollbarMouseMove = useCallback((e) => {
    if (!isScrollbarDragging) return;
    
    const grid = browseGridRef.current;
    const thumb = scrollThumbRef.current;
    if (!grid || !thumb) return;
    
    const deltaY = e.clientY - scrollDragStart.current.y;
    const trackHeight = thumb.parentElement.offsetHeight;
    const thumbHeight = thumb.offsetHeight;
    const maxThumbTop = trackHeight - thumbHeight;
    const scrollRatio = deltaY / maxThumbTop;
    const maxScroll = grid.scrollHeight - grid.clientHeight;
    
    grid.scrollTop = scrollDragStart.current.scrollTop + (scrollRatio * maxScroll);
  }, [isScrollbarDragging]);

  const handleScrollbarMouseUp = useCallback(() => {
    setIsScrollbarDragging(false);
  }, []);

  const handleTrackClick = useCallback((e) => {
    const thumb = scrollThumbRef.current;
    const grid = browseGridRef.current;
    if (!thumb || !grid || e.target === thumb) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const clickPosition = (e.clientY - rect.top) / rect.height;
    const maxScroll = grid.scrollHeight - grid.clientHeight;
    
    grid.scrollTop = clickPosition * maxScroll;
  }, []);

  // Effects
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (isScrollbarDragging) {
      document.addEventListener('mousemove', handleScrollbarMouseMove);
      document.addEventListener('mouseup', handleScrollbarMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleScrollbarMouseMove);
        document.removeEventListener('mouseup', handleScrollbarMouseUp);
      };
    }
  }, [isScrollbarDragging, handleScrollbarMouseMove, handleScrollbarMouseUp]);

  useEffect(() => {
    if (showBrowseModal) {
      const timer = setTimeout(() => {
        updateScrollbar();
        const grid = browseGridRef.current;
        if (grid) {
          grid.addEventListener('scroll', updateScrollbar);
          return () => grid.removeEventListener('scroll', updateScrollbar);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showBrowseModal, updateScrollbar]);

  // Filter and sort videos - use ONLY Firebase videos (no mock data fallback)
  const getFilteredVideos = () => {
    if (firebaseVideos.length === 0) return [];
    let filtered = [...firebaseVideos];
    
    // Apply filter
    if (currentFilter !== 'all') {
      filtered = filtered.filter(video => {
        switch(currentFilter) {
          case 'podcast': return video.type === 'podcast';
          case 'video': return video.type === 'video';
          case 'trending': return video.trending;
          case 'guest': return video.guest;
          case 'clubs': return video.uploadedByEntity === true && (video.entityType === 'networkclub' || video.entityType === 'mastermind' || video.entityType === 'community');
          default: return true;
        }
      });
    }
    
    // Apply search
    if (searchTerm) {
      filtered = filtered.filter(video => 
        video.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        video.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (video.guest && video.guest.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (video.clubName && video.clubName.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      switch(currentSort) {
        case 'newest': 
          return new Date(b.publishDate) - new Date(a.publishDate);
        case 'oldest': 
          return new Date(a.publishDate) - new Date(b.publishDate);
        case 'most-viewed': 
          return b.views - a.views;
        case 'most-shares': 
          return (b.todayShares || 0) - (a.todayShares || 0);
        case 'trending':
          if (a.trending && !b.trending) return -1;
          if (!a.trending && b.trending) return 1;
          return (b.views + (b.todayShares || 0) * 10) - (a.views + (a.todayShares || 0) * 10);
        default: 
          // Relevance
          if (a.mostViewed && !b.mostViewed) return -1;
          if (!a.mostViewed && b.mostViewed) return 1;
          if (a.trending && !b.trending) return -1;
          if (!a.trending && b.trending) return 1;
          return (b.views + (b.todayShares || 0) * 10) - (a.views + (a.todayShares || 0) * 10);
      }
    });
    
    return filtered;
  };

  // Badge Component - Verschillende oranje tinten met 80% opacity en gradient
  const Badge = ({ type, children, className = "" }) => {
    const baseClasses = "text-xs px-2 py-1 rounded font-semibold text-white shadow-lg flex items-center gap-1 backdrop-blur-sm";
    
    // Badge kleuren per type met opacity
    const oranjeGradients = {
      podcast: "bg-[#7A9488]/80 border border-stone-300/60",
      guest: "bg-[#4F6660]/80 border border-[#7A9488]/60", 
      trending: "bg-[#4F6660]/80 border border-[#7A9488]/60",
      mostViewed: "bg-[#7A9488]/80 border border-[#7A9488]/60",
      shares: "bg-[#4F6660]/80 border border-[#7A9488]/60"
    };
    
    const gradientClass = oranjeGradients[type] || oranjeGradients.podcast;
    
    return (
      <div className={`${baseClasses} ${gradientClass} ${className}`}>
        {children}
      </div>
    );
  };

  // Video Card Component
  const VideoCard = ({ video, isGrid = false, isFirstVideo = false }) => {
    // Get video thumbnail - prioritize real thumbnail, fallback to gradient
    const getVideoThumbnail = () => {
      if (video.thumbnailUrl) {
        return video.thumbnailUrl;
      }
      if (video.platform === 'youtube' && video.platformId) {
        return getYouTubeThumbnail(video.platformId);
      }
      return gradients[video.thumbnail] || gradients.gradient1;
    };

    // Get video URL for playback
    const getVideoUrl = () => {
      if (video.videoUrl) {
        return video.videoUrl;
      }
      if (video.platform === 'youtube' && video.platformId) {
        return `https://www.youtube.com/watch?v=${video.platformId}`;
      }
      if (video.platform === 'vimeo' && video.platformId) {
        return `https://vimeo.com/${video.platformId}`;
      }
      return null;
    };

    // Get embed URL for inline playback
    const getEmbedUrl = () => {
      if (video.platform === 'youtube' && video.platformId) {
        return `https://www.youtube.com/embed/${video.platformId}?autoplay=1&rel=0&modestbranding=1`;
      }
      if (video.platform === 'vimeo' && video.platformId) {
        return `https://player.vimeo.com/video/${video.platformId}?autoplay=1&title=0&byline=0&portrait=0`;
      }
      return null;
    };

    // Smart badge logic - slechts 1 badge per video
    const getBadges = () => {
      // Priority 1: Most viewed (highest priority status)
      if (video.mostViewed) {
        return [<Badge key="most-viewed" type="mostViewed">👑 BEST BEKEKEN</Badge>];
      }
      
      // Priority 2: Trending (if not most viewed)
      if (video.trending) {
        return [<Badge key="trending" type="trending">🔥 TRENDING</Badge>];
      }
      
      // Priority 3: Guest feature (marketing value)
      if (video.guest) {
        const firstName = video.guest.split(' ')[0];
        return [<Badge key="guest" type="guest">👋 ONTMOET {firstName}</Badge>];
      }
      
      // Priority 4: Content type (always show for podcasts if no special status)
      if (video.type === 'podcast') {
        return [<Badge key="podcast" type="podcast">🎙️ PODCAST</Badge>];
      }
      
      // Priority 5: High shares (only if nothing else special)
      if ((video.todayShares || 0) > 50) {
        return [<Badge key="shares" type="shares">📤 {video.todayShares || 0} shares</Badge>];
      }
      
      // Geen badge als er niets speciaal is
      return [];
    };

    const badges = getBadges();
    const thumbnailUrl = getVideoThumbnail();
    const videoUrl = getVideoUrl();

    if (isGrid) {
      
      return (
        <div 
          className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer hover:-translate-y-1 border border-gray-100 hover:border-stone-300 group"
          onClick={() => {
            setSelectedVideo(video);
            setShowVideoModal(true);
          }}
        >
          <div className="w-full h-40 relative rounded-t-2xl overflow-hidden">
            {thumbnailUrl && thumbnailUrl.startsWith('http') ? (
              <img 
                src={thumbnailUrl} 
                alt={video.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                onError={(e) => {
                  // Fallback to gradient if image fails to load
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div 
              className={`w-full h-full flex items-center justify-center text-white ${
                thumbnailUrl && thumbnailUrl.startsWith('http') ? 'hidden' : 'flex'
              }`}
              style={{ background: thumbnailUrl && !thumbnailUrl.startsWith('http') ? thumbnailUrl : gradients[video.thumbnail] }}
            >
              {badges.length > 0 && (
                <div className="absolute top-3 right-3 flex flex-col gap-1">
                  {badges}
                </div>
              )}
              {videoUrl && (
                <div className="absolute top-3 left-3">
                  <div className="bg-black/70 backdrop-blur-sm text-white px-2 py-1 rounded-md text-xs font-medium">
                    {video.platform === 'youtube' ? 'YouTube' : video.platform === 'vimeo' ? 'Vimeo' : 'Video'}
                  </div>
                </div>
              )}
              <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors duration-200">
                <div className="bg-white/90 backdrop-blur-sm rounded-full p-4 group-hover:scale-110 transition-transform duration-200">
                  <PlayCircle className="w-10 h-10 text-[#7A9488] fill-[#7A9488]" strokeWidth={2} />
                </div>
              </div>
            </div>
          </div>
          <div className="p-5">
            <h4 className="text-lg font-semibold text-gray-900 mb-2 leading-tight">{video.title}</h4>
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
              <span>{video.views} views</span>
              <span>•</span>
              <span>{video.todayShares || 0} shares</span>
              <span>•</span>
              <span>{formatDate(video.publishDate)}</span>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed line-clamp-2">{video.description}</p>
          </div>
        </div>
      );
    }

    return (
      <div 
        className="video-card bg-white shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer hover:-translate-y-1 hover:scale-[1.02] flex-shrink-0 relative z-10 hover:z-20 group rounded-lg overflow-hidden"
        onClick={() => {
          // If this is the first video, open guided overlay (as per prompt: "first carousel card")
          if (isFirstVideo) {
            setShowGuidedVideoOverlay(true);
          } else {
            setSelectedVideo(video);
            setShowVideoModal(true);
          }
        }}
        style={{ width: '200px', height: '100%' }}
      >
        <div className="w-full h-full relative transition-all duration-200 overflow-hidden">
          {thumbnailUrl && thumbnailUrl.startsWith('http') ? (
            <img 
              src={thumbnailUrl} 
              alt={video.title}
              className="w-full h-full object-cover carousel-card-img"
              onError={(e) => {
                // Fallback to gradient if image fails to load
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className={`w-full h-full flex items-center justify-center text-white text-xl carousel-card-img ${
              thumbnailUrl && thumbnailUrl.startsWith('http') ? 'hidden' : 'flex'
            }`}
            style={{ background: thumbnailUrl && !thumbnailUrl.startsWith('http') ? thumbnailUrl : gradients[video.thumbnail] }}
          >
            {badges.length > 0 && (
              <div className="absolute top-1 right-1 flex flex-col gap-1 z-10">
                {badges}
              </div>
            )}
            {videoUrl && (
              <div className="absolute top-1 left-1 z-10">
                <div className="bg-black bg-opacity-50 text-white px-1 py-0.5 rounded text-xs">
                  {video.platform === 'youtube' ? 'YT' : video.platform === 'vimeo' ? 'VM' : 'V'}
                </div>
              </div>
            )}
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors duration-200">
              <div className="bg-white/90 backdrop-blur-sm rounded-full p-3 group-hover:scale-110 transition-transform duration-200">
                <PlayCircle className="w-8 h-8 text-[#7A9488] fill-[#7A9488]" strokeWidth={2} />
              </div>
            </div>
            {/* Sage overlay — verdwijnt bij hover */}
            <div className="carousel-sage-overlay absolute inset-0 pointer-events-none rounded-lg transition-opacity duration-300 group-hover:opacity-0" style={{ backgroundColor: 'rgba(141,168,160,0.22)' }} />
            {/* Visual indicator for first video (guided video) */}
            {isFirstVideo && (
              <div className="absolute top-2 left-2 z-10">
                <div className="bg-[#4F6660] text-white text-xs px-2 py-1 rounded-full font-semibold shadow-lg flex items-center gap-1">
                  <span>ℹ️</span>
                  <span>Zo werkt het</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Use ONLY Firebase videos - never show mock data if Firebase has videos
  // Only fallback to mock data if Firebase is completely empty AND not loading (for development)
  // Filter out any videos without required fields
  let validFirebaseVideos = firebaseVideos.filter(v => v && v.title && v.id);
  
  // Filter for collaborations mode
  if (filterMode === 'collaborations') {
    validFirebaseVideos = validFirebaseVideos.filter(v => collaborationVideoIds.has(v.id));
  }
  
  const updatedVideoData = updateTrendingStatus(validFirebaseVideos);
  
  // Filter videos by region and limit trending videos to max 20 per region
  const filterVideosByRegion = async (videos) => {
    if (userRegions.length === 0) {
      // If user has no regions, show all videos (no filtering)
      return videos;
    }

    // Separate videos by type
    const nonTrendingVideos = [];
    const trendingVideos = [];

    videos.forEach(video => {
      if (video.trending) {
        trendingVideos.push(video);
      } else {
        nonTrendingVideos.push(video);
      }
    });

    // For trending videos, group by region and limit to 20 per region
    const MAX_TRENDING_PER_REGION = 20;
    const videosByRegion = {};
    const videosWithoutRegion = [];
    const videoIdsSeen = new Set();

    // First, enrich videos with uploader regions if missing
    const enrichedVideos = await Promise.all(trendingVideos.map(async (video) => {
      // If video already has regions, use those
      if (video.regions && video.regions.length > 0) {
        return video;
      }
      if (video.region) {
        return { ...video, regions: [video.region] };
      }
      
      // If video has uploaderRegions stored, use those
      if (video.uploaderRegions && video.uploaderRegions.length > 0) {
        return { ...video, regions: video.uploaderRegions };
      }
      
      // If video has userId, fetch uploader's regions from profile
      if (video.userId) {
        try {
          const db = getFirestore();
          const userDoc = await getDoc(doc(db, 'userProfiles', video.userId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const uploaderRegions = userData.selectedRegions || [];
            if (uploaderRegions.length > 0) {
              return { ...video, regions: uploaderRegions, uploaderRegions };
            }
          }
        } catch (error) {
          console.error(`Error fetching regions for uploader ${video.userId}:`, error);
        }
      }
      
      // No regions found - return as is (will be added to fallback)
      return video;
    }));

    enrichedVideos.forEach(video => {
      const videoRegions = video.regions || [];
      
      if (videoRegions.length === 0) {
        // Video without region and without uploader info - add to fallback list
        videosWithoutRegion.push(video);
        return;
      }

      // Check if video matches any of user's regions
      const matchingRegions = videoRegions.filter(vr => userRegions.includes(vr));
      
      if (matchingRegions.length > 0) {
        // Video matches user's region - add to first matching region bucket (to avoid duplicates)
        const firstMatchingRegion = matchingRegions[0];
        if (!videosByRegion[firstMatchingRegion]) {
          videosByRegion[firstMatchingRegion] = [];
        }
        if (!videoIdsSeen.has(video.id)) {
          videosByRegion[firstMatchingRegion].push(video);
          videoIdsSeen.add(video.id);
        }
      } else {
        // Video has regions but doesn't match user's regions - don't show
        // (or add to fallback if you want to show videos from other regions)
      }
    });

    // Limit trending videos to max 20 per region and sort by engagement
    const filteredTrendingByRegion = [];
    
    Object.keys(videosByRegion).forEach(region => {
      const regionVideos = videosByRegion[region];
      // Sort by engagement score (if available) or keep original order
      const sorted = regionVideos.sort((a, b) => {
        const scoreA = a.todayEngagement || 0;
        const scoreB = b.todayEngagement || 0;
        return scoreB - scoreA;
      });
      // Take top 20
      filteredTrendingByRegion.push(...sorted.slice(0, MAX_TRENDING_PER_REGION));
    });

    // Videos without region (as fallback, max 20 total)
    const videosWithoutRegionLimited = videosWithoutRegion
      .sort((a, b) => (b.todayEngagement || 0) - (a.todayEngagement || 0))
      .slice(0, MAX_TRENDING_PER_REGION);

    // Combine: trending by region (max 20 each) + videos without region (as fallback, max 20 total) + non-trending (all)
    return [...filteredTrendingByRegion, ...videosWithoutRegionLimited, ...nonTrendingVideos];
  };

  // Apply region filter asynchronously (enriches videos with uploader regions)
  useEffect(() => {
    const applyRegionFilter = async () => {
      if (validFirebaseVideos.length === 0) {
        setFilteredVideos([]);
        return;
      }

      // Only apply region filter if regionFilter is set to 'myRegion'
      if (regionFilter === 'myRegion') {
        const filtered = await filterVideosByRegion(updatedVideoData);
        setFilteredVideos(filtered);
      } else {
        // Show all videos when regionFilter is 'all'
        setFilteredVideos(updatedVideoData);
      }
    };

    applyRegionFilter();
  }, [firebaseVideos, userRegions, regionFilter]);

  const videosForCarousel = filteredVideos.length > 0 
    ? filteredVideos
    : (firebaseLoading ? [] : validFirebaseVideos.length > 0 ? updatedVideoData : []);
  
  const rankedVideos = rankVideos(videosForCarousel);
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const searchedRankedVideos = normalizedSearchQuery
    ? rankedVideos.filter((video) => [
        video.title,
        video.description,
        video.guest,
        video.type,
        video.region,
        ...(Array.isArray(video.regions) ? video.regions : []),
        ...(Array.isArray(video.uploaderRegions) ? video.uploaderRegions : []),
        video.uploaderName,
        video.clubName,
      ].filter(Boolean).join(' ').toLowerCase().includes(normalizedSearchQuery))
    : rankedVideos;

  // Mobile detection
  const [isMobile, setIsMobile] = React.useState(() => isPhoneViewport());
  React.useEffect(() => {
    const check = () => setIsMobile(isPhoneViewport());
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, []);
  const duplicatedVideos = [...searchedRankedVideos, ...searchedRankedVideos];
  
  // Debug logging
  console.log('=== VIDEO DEBUG INFO ===');
  console.log('Firebase videos count:', firebaseVideos.length);
  console.log('Valid Firebase videos count:', validFirebaseVideos.length);
  console.log('User regions:', userRegions);
  console.log('Videos for carousel count (after region filter):', videosForCarousel.length);
  console.log('Videos for carousel:', videosForCarousel);
  console.log('Ranked videos count:', rankedVideos.length);
  console.log('Ranked videos:', rankedVideos);
  console.log('Duplicated videos count:', duplicatedVideos.length);
  console.log('========================');
  const finalFilteredVideos = getFilteredVideos();

  return (
    <>
      {/* CSS Animations */}
      <style>{`
        @keyframes scroll-seamless {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .video-card { flex-shrink: 0; }
        .custom-scrollbar::-webkit-scrollbar { display: none; }
        .custom-scrollbar { scrollbar-width: none; -ms-overflow-style: none; }
      `}</style>

      {/* ── MOBILE: Vertical stacked list ────────────────────────────── */}
      {isMobile && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0, minHeight: '100%', background: '#fff' }}>
          {searchedRankedVideos.length === 0 && !firebaseLoading && (
            <div style={{ padding: '16px', textAlign: 'center', color: '#9CA3AF', fontSize: '13px' }}>
              Geen video's gevonden
            </div>
          )}
          {firebaseLoading && (
            <div style={{ padding: '16px', textAlign: 'center', color: '#9CA3AF', fontSize: '13px' }}>
              Video's laden…
            </div>
          )}
          {searchedRankedVideos.map((video, index) => {
            const videoUrl = video.youtubeId
              ? `https://www.youtube.com/watch?v=${video.youtubeId}`
              : video.vimeoId
                ? `https://vimeo.com/${video.vimeoId}`
                : video.url || '';
            const thumbUrl = video.youtubeId
              ? `https://img.youtube.com/vi/${video.youtubeId}/mqdefault.jpg`
              : video.thumbnail?.startsWith('http') ? video.thumbnail : '';
            return (
              <button
                key={video.id}
                onClick={() => { setSelectedVideo(video); setShowVideoModal(true); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 12px',
                  borderBottom: '1px solid #F4F2ED',
                  background: '#fff',
                  border: 'none',
                  borderBottomWidth: '1px',
                  borderBottomStyle: 'solid',
                  borderBottomColor: '#F4F2ED',
                  cursor: 'pointer',
                  textAlign: 'left',
                  width: '100%',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                {/* Thumbnail */}
                <div style={{
                  width: '88px', height: '60px', borderRadius: '8px',
                  overflow: 'hidden', flexShrink: 0, position: 'relative',
                  background: '#E8EDE4',
                }}>
                  {thumbUrl ? (
                    <img
                      src={thumbUrl}
                      alt={video.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : null}
                  <div style={{
                    position: 'absolute', inset: 0, display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(0,0,0,0.15)',
                  }}>
                    <div style={{
                      background: 'rgba(255,255,255,0.9)', borderRadius: '50%',
                      width: '24px', height: '24px', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                    }}>
                      <svg width="10" height="12" viewBox="0 0 10 12" fill="#4F6660">
                        <path d="M0 0l10 6-10 6z" />
                      </svg>
                    </div>
                  </div>
                  {video.badge && (
                    <span style={{
                      position: 'absolute', top: '3px', right: '3px',
                      background: 'rgba(79,102,96,0.85)', color: '#fff',
                      fontSize: '9px', fontWeight: 700, padding: '1px 4px',
                      borderRadius: '4px',
                    }}>{video.badge}</span>
                  )}
                </div>
                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{
                    fontSize: '13px', fontWeight: 600, color: '#1F2937',
                    margin: '0 0 3px', overflow: 'hidden', display: '-webkit-box',
                    WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  }}>{video.title}</p>
                  {video.guest && (
                    <p style={{ fontSize: '11px', color: '#7A9488', margin: '0 0 2px' }}>
                      👋 {video.guest}
                    </p>
                  )}
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>
                    {video.views || 0} views
                    {video.type ? ` · ${video.type}` : ''}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* ── DESKTOP: Horizontal auto-scrolling carousel ───────────────── */}
      {!isMobile && (
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden relative">
        <div 
          ref={containerRef}
          className="relative overflow-hidden cursor-grab active:cursor-grabbing"
          onMouseDown={handleMouseDown}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{ cursor: isDragging ? 'grabbing' : 'grab', height: '120px' }}
        >
          <div 
            ref={trackRef}
            className="flex h-full items-center transition-transform duration-200"
            style={{
              gap: '12px',
              transform: isDragging ? `translateX(${currentTransform}px)` : undefined,
              animation: isDragging ? 'none' : `scroll-seamless ${animationSpeed}s linear infinite`,
              animationPlayState: isDragging ? 'paused' : isPlaying ? 'running' : 'paused',
              width: 'max-content'
            }}
          >
            {duplicatedVideos.map((video, index) => {
              // Check if this is the first video in the original rankedVideos array
              // (not the duplicated second set)
              const isFirstInOriginal = index === 0;
              // First video is always the guided video (as per prompt: "first carousel card")
              // Ideally it should have "Zo werkt SuperKonnected" in title, but we use first video as fallback
              const isGuidedVideo = isFirstInOriginal;
              return (
                <VideoCard 
                  key={`${video.id}-${index}`} 
                  video={video} 
                  isFirstVideo={isGuidedVideo}
                />
              );
            })}
          </div>
          
          {/* Controls - Only show on hover; hidden on touch/mobile */}
          <div className={`controls hidden md:flex absolute bottom-3 right-6 items-center gap-2 z-20 transition-opacity duration-200 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
            {/* Speed Control */}
            <div className="bg-white/80 backdrop-blur-sm rounded-lg px-2 py-1 flex items-center gap-2 border border-stone-200 shadow-sm">
              <Zap className="w-3.5 h-3.5 text-stone-500" strokeWidth={2} />
              <input
                type="range"
                min="10"
                max="120"
                value={animationSpeed}
                onChange={(e) => setAnimationSpeed(Number(e.target.value))}
                className="w-20 h-1 bg-stone-200 rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #4F6660 0%, #4F6660 ${((animationSpeed - 10) / 110) * 100}%, #E8EDE4 ${((animationSpeed - 10) / 110) * 100}%, #E8EDE4 100%)`
                }}
                aria-label="Animatie snelheid"
              />
              <span className="text-xs text-stone-500 w-8 text-right">{animationSpeed}s</span>
            </div>
            <button 
              className="bg-white/80 backdrop-blur-sm text-stone-500 px-3 py-1.5 rounded-lg text-sm hover:text-stone-700 hover:bg-white transition-all border border-stone-200 shadow-sm"
              onClick={() => setIsPlaying(!isPlaying)}
              aria-label={isPlaying ? 'Pauzeren' : 'Afspelen'}
            >
              {isPlaying ? (
                <Pause className="w-4 h-4" strokeWidth={2} />
              ) : (
                <PlayCircle className="w-4 h-4" strokeWidth={2} />
              )}
            </button>
            <button 
              className="bg-white/80 backdrop-blur-sm text-stone-500 px-3 py-1.5 rounded-lg text-sm hover:text-stone-700 hover:bg-white transition-all border border-stone-200 shadow-sm"
              onClick={() => {
                setCurrentTransform(0);
                setIsPlaying(true);
              }}
              aria-label="Reset"
            >
              <RefreshCw className="w-4 h-4" strokeWidth={2} />
            </button>
            <button 
              className="bg-stone-600/80 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg text-sm hover:bg-stone-700/90 transition-all shadow-sm flex items-center gap-1.5 border border-stone-500/30"
              onClick={() => setShowBrowseModal(true)}
              aria-label="Bladeren door videos"
            >
              <Search className="w-4 h-4" strokeWidth={2} />
              <span>Bladeren</span>
            </button>
          </div>
        </div>
      </div>
      )} {/* end !isMobile desktop carousel */}

      {/* Video Modal - Inline Video Player */}
      {showVideoModal && selectedVideo && (
        <div 
          className={`fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[9999] backdrop-blur-sm p-4 ${isFullscreen ? 'p-0' : ''}`}
          onClick={() => {
            setShowVideoModal(false);
            setIsFullscreen(false);
          }}
        >
          <div 
            className={`bg-white rounded-2xl shadow-2xl relative flex flex-col transition-all duration-300 ${
              isFullscreen 
                ? 'w-full h-full max-w-none max-h-none rounded-none' 
                : 'max-w-4xl w-full max-h-[90vh]'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header Controls */}
            <div className="flex items-center justify-between p-4 bg-gray-50 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 truncate">{selectedVideo.title}</h3>
              <div className="flex items-center gap-2">
                <button 
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 w-8 h-8 rounded-full flex items-center justify-center font-bold transition-colors"
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                >
                  {isFullscreen ? '⤓' : '⤢'}
                </button>
                <button 
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 w-8 h-8 rounded-full flex items-center justify-center font-bold transition-colors"
                  onClick={() => {
                    setShowVideoModal(false);
                    setIsFullscreen(false);
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            {/* Video Player */}
            <div className={`relative bg-black ${isFullscreen ? 'flex-1' : 'h-96'}`}>
              {(() => {
                // Get embed URL for the selected video
                const getSelectedVideoEmbedUrl = () => {
                  if (selectedVideo.platform === 'youtube' && selectedVideo.platformId) {
                    return `https://www.youtube.com/embed/${selectedVideo.platformId}?autoplay=1&rel=0&modestbranding=1&showinfo=0`;
                  }
                  if (selectedVideo.platform === 'vimeo' && selectedVideo.platformId) {
                    return `https://player.vimeo.com/video/${selectedVideo.platformId}?autoplay=1&title=0&byline=0&portrait=0`;
                  }
                  return null;
                };

                const embedUrl = getSelectedVideoEmbedUrl();
                
                if (embedUrl) {
                  return (
                    <iframe
                      src={embedUrl}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      title={selectedVideo.title}
                    />
                  );
                } else {
                  // Fallback for videos without embed support
                  return (
                    <div className="w-full h-full flex items-center justify-center text-white text-6xl relative">
                      <div 
                        className="w-full h-full flex items-center justify-center"
                        style={{ background: gradients[selectedVideo.thumbnail] || gradients.gradient1 }}
                      >
                        ▶
                      </div>
                      <div className="absolute bottom-4 left-4 right-4 bg-black bg-opacity-50 text-white p-4 rounded-lg">
                        <p className="text-sm">Video afspelen niet beschikbaar. Klik om naar de originele pagina te gaan.</p>
                        <button 
                          className="mt-2 bg-[#7A9488] hover:bg-[#4F6660] text-white px-4 py-2 rounded-lg text-sm"
                          onClick={() => {
                            const videoUrl = selectedVideo.videoUrl || 
                              (selectedVideo.platform === 'youtube' && selectedVideo.platformId ? 
                                `https://www.youtube.com/watch?v=${selectedVideo.platformId}` : 
                                selectedVideo.platform === 'vimeo' && selectedVideo.platformId ? 
                                `https://vimeo.com/${selectedVideo.platformId}` : null);
                            if (videoUrl) {
                              window.open(videoUrl, '_blank');
                            }
                          }}
                        >
                          Open in {selectedVideo.platform === 'youtube' ? 'YouTube' : selectedVideo.platform === 'vimeo' ? 'Vimeo' : 'Browser'}
                        </button>
                      </div>
                    </div>
                  );
                }
              })()}
            </div>

            {/* Video Info */}
            {!isFullscreen && (
              <div className="p-6 flex-1 overflow-y-auto">
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span>👀 {selectedVideo.views || 0} views</span>
                    <span>•</span>
                    <span>📤 {selectedVideo.todayShares || 0} shares</span>
                    <span>•</span>
                    <span>📅 {formatDate(selectedVideo.publishDate)}</span>
                  </div>
                  {selectedVideo.guest && (
                    <div className="px-3 py-1 bg-stone-100 text-[#4F6660] rounded-full text-sm font-medium">
                      👋 {selectedVideo.guest}
                    </div>
                  )}
                </div>
                <p className="text-gray-600 leading-relaxed mb-6">{selectedVideo.description}</p>

                {/* Connectie Sectie - Uploader */}
                {uploaderProfile && (
                  <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {uploaderProfile.photoURL ? (
                          <img 
                            src={uploaderProfile.photoURL} 
                            alt={uploaderProfile.displayName || 'Uploader'}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-semibold">
                            {(uploaderProfile.displayName || 'U').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900">Geüpload door</p>
                          <p className="text-sm text-gray-600">{uploaderProfile.displayName || 'Onbekend'}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            window.dispatchEvent(new CustomEvent('navigate', { 
                              detail: { page: 'mijnverhaal', userId: uploaderProfile.id } 
                            }));
                            setShowVideoModal(false);
                          }}
                          className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors"
                        >
                          👤 Profiel
                        </button>
                        {uploaderProfile.id !== getAuth().currentUser?.uid && (
                          <button
                            onClick={async () => {
                              if (sendingConnectionRequest) return;
                              setSendingConnectionRequest(true);
                              try {
                                const { connectionRequestService } = await import('../services/connectionRequestService');
                                await connectionRequestService.sendConnectionRequest(
                                  getAuth().currentUser.uid,
                                  uploaderProfile.id,
                                  `Ik zag je video "${selectedVideo.title}" en zou graag willen connecten!`
                                );
                                alert('Connectieverzoek verzonden!');
                              } catch (error) {
                                console.error('Error sending connection request:', error);
                                if (error.message === 'Connection request already exists') {
                                  alert('Je hebt al een connectieverzoek verstuurd naar deze persoon.');
                                } else {
                                  alert('Er is een fout opgetreden. Probeer het opnieuw.');
                                }
                              } finally {
                                setSendingConnectionRequest(false);
                              }
                            }}
                            disabled={sendingConnectionRequest}
                            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {sendingConnectionRequest ? '⏳...' : '🤝 Connect'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Connectie Sectie - Guest */}
                {guestProfile && guestProfile.id !== uploaderProfile?.id && (
                  <div className="mb-4 p-4 bg-stone-50 rounded-lg border border-stone-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {guestProfile.photoURL ? (
                          <img 
                            src={guestProfile.photoURL} 
                            alt={guestProfile.displayName || selectedVideo.guest}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#7A9488] to-red-500 flex items-center justify-center text-white font-semibold">
                            {(guestProfile.displayName || selectedVideo.guest || 'G').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900">Gast in deze video</p>
                          <p className="text-sm text-gray-600">{guestProfile.displayName || selectedVideo.guest}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            window.dispatchEvent(new CustomEvent('navigate', { 
                              detail: { page: 'mijnverhaal', userId: guestProfile.id } 
                            }));
                            setShowVideoModal(false);
                          }}
                          className="px-4 py-2 bg-white border border-stone-300 text-gray-700 rounded-lg hover:bg-stone-50 text-sm font-medium transition-colors"
                        >
                          👤 Profiel
                        </button>
                        {guestProfile.id !== getAuth().currentUser?.uid && (
                          <button
                            onClick={async () => {
                              if (sendingConnectionRequest) return;
                              setSendingConnectionRequest(true);
                              try {
                                const { connectionRequestService } = await import('../services/connectionRequestService');
                                await connectionRequestService.sendConnectionRequest(
                                  getAuth().currentUser.uid,
                                  guestProfile.id,
                                  `Ik zag je in de video "${selectedVideo.title}" en zou graag willen connecten!`
                                );
                                alert('Connectieverzoek verzonden!');
                              } catch (error) {
                                console.error('Error sending connection request:', error);
                                if (error.message === 'Connection request already exists') {
                                  alert('Je hebt al een connectieverzoek verstuurd naar deze persoon.');
                                } else {
                                  alert('Er is een fout opgetreden. Probeer het opnieuw.');
                                }
                              } finally {
                                setSendingConnectionRequest(false);
                              }
                            }}
                            disabled={sendingConnectionRequest}
                            className="px-4 py-2 bg-[#7A9488] text-white rounded-lg hover:bg-[#4F6660] text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {sendingConnectionRequest ? '⏳...' : '🤝 Connect'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Browse Modal */}
      {showBrowseModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[9998] backdrop-blur-sm p-4"
          onClick={() => setShowBrowseModal(false)}
        >
          <div 
            className="bg-white rounded-3xl w-full max-w-7xl h-5/6 flex flex-col overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-8 py-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-3xl font-semibold text-gray-900">🔍 Browse All Content</h3>
              <div className="flex items-center gap-3">
                {isAdmin && (
                  <button 
                    className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:from-purple-600 hover:to-blue-700 transition-all shadow-lg"
                    onClick={() => {
                      setShowBrowseModal(false);
                      setShowAdminPanel(true);
                    }}
                  >
                    ⚙️ Admin Panel
                  </button>
                )}
                <button 
                  className="bg-white bg-opacity-90 w-10 h-10 rounded-full flex items-center justify-center font-bold text-gray-700 hover:bg-opacity-100 text-xl"
                  onClick={() => setShowBrowseModal(false)}
                >
                  ×
                </button>
              </div>
            </div>
            
            {/* Search Section */}
            <div className="px-8 py-6 border-b border-gray-200 bg-white">
              <div className="relative mb-5">
                <input 
                  type="text"
                  className="w-full pl-12 pr-5 py-3 border-2 border-gray-200 rounded-xl text-lg bg-gray-50 focus:bg-white focus:border-[#7A9488] focus:outline-none focus:ring-2 focus:ring-[#7A9488]/20 transition-all"
                  placeholder="Zoek podcasts, video's, gasten..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-xl">🔍</div>
              </div>
              
              <div className="flex gap-2 flex-wrap">
                {[
                  { key: 'all', label: 'Alles' },
                  { key: 'podcast', label: 'Podcasts' },
                  { key: 'video', label: "Video's" },
                  { key: 'trending', label: 'Trending' },
                  { key: 'guest', label: 'Met Gasten' },
                  { key: 'clubs', label: 'Van Clubs' }
                ].map(filter => (
                  <button
                    key={filter.key}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      currentFilter === filter.key
                        ? 'bg-gradient-to-r from-[#7A9488] to-yellow-600 text-white shadow-lg'
                        : 'bg-white border-2 border-gray-200 text-gray-600 hover:border-[#7A9488] hover:text-[#7A9488]'
                    }`}
                    onClick={() => setCurrentFilter(filter.key)}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Content Grid */}
            <div className="flex-1 overflow-hidden relative">
              <div 
                ref={browseGridRef}
                className="p-8 h-full overflow-y-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 custom-scrollbar"
                style={{ scrollbarWidth: 'none' }}
              >
                {finalFilteredVideos.map(video => (
                  <VideoCard key={video.id} video={video} isGrid={true} />
                ))}
              </div>
            </div>
            
            {/* Footer */}
            <div className="px-8 py-5 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>{finalFilteredVideos.length} video's gevonden</span>
                <span>SuperKonnected - The Movement</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Panel Modal */}
      {showAdminPanel && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[9999] backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-11/12 max-w-4xl h-5/6 flex flex-col overflow-hidden shadow-2xl">
            {/* Admin Header */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 px-8 py-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-3xl font-semibold text-gray-900">⚙️ Video Management</h3>
              <div className="flex items-center gap-3">
                <button 
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-600 transition-all"
                  onClick={() => {
                    setShowAdminPanel(false);
                    setShowBrowseModal(true);
                  }}
                >
                  ← Terug naar Browse
                </button>
                <button 
                  className="bg-white bg-opacity-90 w-10 h-10 rounded-full flex items-center justify-center font-bold text-gray-700 hover:bg-opacity-100 text-xl"
                  onClick={() => setShowAdminPanel(false)}
                >
                  ×
                </button>
              </div>
            </div>
            
            {/* Admin Tabs */}
            <div className="flex border-b border-gray-200">
              <button 
                className={`px-6 py-3 font-medium ${adminActiveTab === 'upload' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-blue-600'}`}
                onClick={() => setAdminActiveTab('upload')}
              >
                {editingVideo ? '✏️ Bewerk Video' : '📹 Upload Video'}
              </button>
              <button 
                className={`px-6 py-3 font-medium ${adminActiveTab === 'manage' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-blue-600'}`}
                onClick={() => setAdminActiveTab('manage')}
              >
                📋 Video Management
              </button>
            </div>
            
            {/* Admin Content */}
            <div className="flex-1 p-8 overflow-y-auto">
              {/* Error Display */}
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <span className="text-red-500 mr-2">⚠️</span>
                    <span className="text-red-700">{error}</span>
                    <button 
                      className="ml-auto text-red-500 hover:text-red-700"
                      onClick={() => setError(null)}
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}
              {adminActiveTab === 'upload' && (
                <div className="max-w-2xl">
                  {/* Upload Type Selection */}
                  <div className="mb-6">
                    <div className="flex bg-gray-100 rounded-lg p-1">
                      <button 
                        className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${
                          uploadType === 'file' 
                            ? 'bg-white text-blue-600 shadow-sm' 
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                        onClick={() => setUploadType('file')}
                      >
                        📁 Bestand Uploaden
                      </button>
                      <button 
                        className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${
                          uploadType === 'link' 
                            ? 'bg-white text-blue-600 shadow-sm' 
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                        onClick={() => setUploadType('link')}
                      >
                        🔗 Video Link
                      </button>
                    </div>
                  </div>

                  {uploadType === 'file' ? (
                    /* Drag & Drop Upload */
                    <div className="border-2 border-dashed border-gray-300 rounded-2xl p-12 text-center hover:border-blue-400 transition-colors">
                      <div className="text-6xl mb-4">📁</div>
                      <h4 className="text-xl font-semibold mb-2">Sleep video's hiernaartoe</h4>
                      <p className="text-gray-600 mb-4">Of klik om bestanden te selecteren</p>
                      <button className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600">
                        Selecteer Bestanden
                      </button>
                    </div>
                  ) : (
                    /* Video Link Input */
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Video Link (YouTube, Vimeo, etc.)
                        </label>
                        <input 
                          type="url"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="https://www.youtube.com/watch?v=... of https://vimeo.com/..."
                          value={videoLink}
                          onChange={(e) => handleLinkChange(e.target.value)}
                        />
                        <p className="text-sm text-gray-500 mt-1">
                          Ondersteunde platforms: YouTube, Vimeo
                        </p>
                      </div>

                      {/* Link Preview */}
                      {linkPreview && (
                        <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                          <h4 className="font-medium text-gray-900 mb-2">Preview:</h4>
                          <div className="flex gap-4">
                            <img 
                              src={linkPreview.thumbnail} 
                              alt="Video thumbnail"
                              className="w-32 h-20 object-cover rounded-lg"
                            />
                            <div className="flex-1">
                              <h5 className="font-medium text-gray-900">{linkPreview.title}</h5>
                              <p className="text-sm text-gray-600">{linkPreview.description}</p>
                              <div className="flex items-center gap-2 mt-2">
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                                  {linkPreview.type === 'youtube' ? 'YouTube' : 'Vimeo'}
                                </span>
                                <span className="text-xs text-gray-500">ID: {linkPreview.id}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Video Details Form */}
                  <div className="mt-8 space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Video Titel</label>
                      <input 
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                        placeholder="Vul video titel in..." 
                        value={videoFormData.title}
                        onChange={(e) => setVideoFormData({...videoFormData, title: e.target.value})}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Beschrijving</label>
                      <textarea 
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-24" 
                        placeholder="Beschrijf je video..."
                        value={videoFormData.description}
                        onChange={(e) => setVideoFormData({...videoFormData, description: e.target.value})}
                      ></textarea>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Content Type</label>
                        <select 
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          value={videoFormData.type}
                          onChange={(e) => setVideoFormData({...videoFormData, type: e.target.value})}
                        >
                          <option value="podcast">🎙️ Podcast</option>
                          <option value="video">📹 Video</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Gast (optioneel)</label>
                        <input 
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                          placeholder="Naam van gast..." 
                          value={videoFormData.guest}
                          onChange={(e) => setVideoFormData({...videoFormData, guest: e.target.value})}
                        />
                      </div>
                    </div>

                    {/* Badge Management */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Badge Selectie</label>
                      <div className="space-y-4">
                        <select 
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          value={selectedBadge}
                          onChange={(e) => setSelectedBadge(e.target.value)}
                        >
                          <option value="">Geen badge</option>
                          {availableBadges.map((badge, index) => (
                            <option key={index} value={badge}>{badge}</option>
                          ))}
                        </select>
                        
                        {/* Custom Badge Creation */}
                        <div className="flex gap-2">
                          <input 
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                            placeholder="Nieuwe badge tekst (bijv. 👋 ONTMOET Sarah)..." 
                            value={customBadgeText}
                            onChange={(e) => setCustomBadgeText(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                addCustomBadge();
                              }
                            }}
                          />
                          <button 
                            className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                            onClick={addCustomBadge}
                            disabled={!customBadgeText.trim()}
                          >
                            + Toevoegen
                          </button>
                        </div>
                        
                        {/* Available Badges Display */}
                        <div>
                          <p className="text-sm text-gray-600 mb-2">Beschikbare badges:</p>
                          <div className="flex flex-wrap gap-2">
                            {availableBadges.map((badge, index) => (
                              <div key={index} className="flex items-center gap-1 bg-white border border-gray-200 rounded-full px-2 py-1 hover:border-red-300 transition-colors">
                                <span className="px-2 py-1 bg-stone-100 text-[#4F6660] rounded-full text-sm">
                                  {badge}
                                </span>
                                <button 
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded-full transition-colors"
                                  onClick={() => removeBadge(badge)}
                                  title="Verwijder badge"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Publish Options */}
                    <div className="flex gap-4 pt-6 border-t border-gray-200">
                      <button 
                        className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600"
                        onClick={() => {
                          setVideoFormData({
                            title: '',
                            description: '',
                            type: 'podcast',
                            guest: '',
                            badge: ''
                          });
                          setSelectedBadge('');
                          setEditingVideo(null);
                        }}
                      >
                        🔄 Reset
                      </button>
                        <button 
                          className={`px-6 py-2 rounded-lg transition-colors ${
                            firebaseLoading 
                              ? 'bg-gray-400 cursor-not-allowed' 
                              : 'bg-blue-500 hover:bg-blue-600'
                          } text-white`}
                          onClick={handleSaveVideo}
                          disabled={firebaseLoading}
                        >
                          {firebaseLoading ? '⏳ Bezig...' : (editingVideo ? '💾 Opslaan' : '📤 Publiceren')}
                        </button>
                      {editingVideo && (
                        <button 
                          className="bg-purple-500 text-white px-6 py-2 rounded-lg hover:bg-purple-600"
                          onClick={() => setAdminActiveTab('manage')}
                        >
                          ← Terug naar Overzicht
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {adminActiveTab === 'manage' && (
                <div className="max-w-7xl">
                  {/* Header with Analytics Toggle */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-2xl font-semibold text-gray-900">📋 Video Management</h3>
                      <button 
                        className={`px-4 py-2 rounded-lg font-medium transition-all ${
                          showAnalytics 
                            ? 'bg-blue-500 text-white' 
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                        onClick={() => setShowAnalytics(!showAnalytics)}
                      >
                        {showAnalytics ? '📊 Analytics Verbergen' : '📊 Analytics Tonen'}
                      </button>
                    </div>
                    
                    {/* Analytics Cards */}
                    {showAnalytics && (
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
                        <div className="bg-blue-50 p-4 rounded-xl">
                          <h4 className="text-sm font-medium text-blue-900 mb-1">Totaal Views</h4>
                          <p className="text-2xl font-bold text-blue-600">{getAnalytics().totalViews.toLocaleString()}</p>
                        </div>
                        <div className="bg-stone-50 p-4 rounded-xl">
                          <h4 className="text-sm font-medium text-[#4A5A3D] mb-1">Totaal Shares</h4>
                          <p className="text-2xl font-bold text-[#4F6660]">{getAnalytics().totalShares.toLocaleString()}</p>
                        </div>
                        <div className="bg-green-50 p-4 rounded-xl">
                          <h4 className="text-sm font-medium text-green-900 mb-1">Videos</h4>
                          <p className="text-2xl font-bold text-green-600">{getAnalytics().totalVideos}</p>
                        </div>
                        <div className="bg-purple-50 p-4 rounded-xl">
                          <h4 className="text-sm font-medium text-purple-900 mb-1">Podcasts</h4>
                          <p className="text-2xl font-bold text-purple-600">{getAnalytics().podcastCount}</p>
                        </div>
                        <div className="bg-red-50 p-4 rounded-xl">
                          <h4 className="text-sm font-medium text-red-900 mb-1">Videos</h4>
                          <p className="text-2xl font-bold text-red-600">{getAnalytics().videoCount}</p>
                        </div>
                        <div className="bg-yellow-50 p-4 rounded-xl">
                          <h4 className="text-sm font-medium text-yellow-900 mb-1">Trending</h4>
                          <p className="text-2xl font-bold text-yellow-600">{getAnalytics().trendingCount}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Controls */}
                    <div className="flex gap-4 mb-4">
                      <input 
                        type="text" 
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                        placeholder="Zoek videos..." 
                        value={videoManagementSearch}
                        onChange={(e) => setVideoManagementSearch(e.target.value)}
                      />
                      <select 
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={videoManagementTypeFilter}
                        onChange={(e) => setVideoManagementTypeFilter(e.target.value)}
                      >
                        <option value="all">Alle Types</option>
                        <option value="podcast">🎙️ Podcast</option>
                        <option value="video">📹 Video</option>
                      </select>
                      <select 
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value)}
                      >
                        <option value="newest">Nieuwste eerst</option>
                        <option value="oldest">Oudste eerst</option>
                        <option value="title">Titel A-Z</option>
                        <option value="views">Meest bekeken</option>
                        <option value="shares">Meest gedeeld</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Video Grid with Better Representation */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {getSortedVideos().map((video) => {
                      // Get video thumbnail - prioritize real thumbnail, fallback to gradient
                      const getVideoThumbnail = () => {
                        // 1. Use stored thumbnailUrl if available
                        if (video.thumbnailUrl && video.thumbnailUrl.startsWith('http')) {
                          return video.thumbnailUrl;
                        }
                        
                        // 2. Generate YouTube thumbnail if we have platformId
                        if (video.platform === 'youtube' && video.platformId) {
                          return getYouTubeThumbnail(video.platformId);
                        }
                        
                        // 3. Try to extract YouTube ID from videoUrl if available
                        if (video.videoUrl && (video.videoUrl.includes('youtube.com') || video.videoUrl.includes('youtu.be'))) {
                          const videoIdInfo = extractVideoId(video.videoUrl);
                          if (videoIdInfo && videoIdInfo.type === 'youtube' && videoIdInfo.id) {
                            return getYouTubeThumbnail(videoIdInfo.id);
                          }
                        }
                        
                        // 4. Fallback to gradient
                        return gradients[video.thumbnail] || gradients.gradient1;
                      };

                      // Get badge for this video (prioritize saved badge, then fallback to properties)
                      const getVideoBadge = () => {
                        // First check if video has a saved badge
                        if (video.badge) return video.badge;
                        
                        // Fallback to property-based badges
                        if (video.mostViewed) return '👑 BEST BEKEKEN';
                        if (video.trending) return '🔥 TRENDING';
                        if (video.guest) return `👋 ONTMOET ${video.guest.split(' ')[0]}`;
                        if (video.type === 'podcast') return '🎙️ PODCAST';
                        if ((video.todayShares || 0) > 50) return `📤 ${video.todayShares || 0} shares`;
                        return null;
                      };
                      
                      const videoBadge = getVideoBadge();
                      const thumbnailUrl = getVideoThumbnail();
                      const hasRealThumbnail = thumbnailUrl && (thumbnailUrl.startsWith('http') || thumbnailUrl.startsWith('https'));
                      
                      return (
                        <div key={video.id} className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-lg transition-all duration-200 hover:-translate-y-1">
                          <div className="relative">
                            <div className="w-full h-40 rounded-t-xl overflow-hidden relative">
                              {hasRealThumbnail ? (
                                <img 
                                  src={thumbnailUrl} 
                                  alt={video.title}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    // Fallback to gradient if image fails to load
                                    e.target.style.display = 'none';
                                    const fallback = e.target.nextElementSibling;
                                    if (fallback) fallback.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div 
                                className={`w-full h-full flex items-center justify-center text-white text-3xl ${
                                  hasRealThumbnail ? 'hidden' : 'flex'
                                }`}
                                style={{ background: gradients[video.thumbnail] || gradients.gradient1 }}
                              >
                                ▶
                              </div>
                              {videoBadge && (
                                <div className="absolute top-2 right-2 z-10">
                                  <span className="px-2 py-1 bg-gradient-to-r from-[#7A9488]/80 to-[#4F6660]/80 text-white text-xs rounded font-semibold shadow-lg">
                                    {videoBadge}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="p-4">
                            <h4 className="font-semibold text-gray-900 mb-2 line-clamp-2 text-sm">{video.title}</h4>
                            <p className="text-xs text-gray-600 mb-3 line-clamp-2">{video.description}</p>
                            <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                              <span>👀 {video.views}</span>
                              <span>📤 {video.todayShares || 0} shares</span>
                              <span className="capitalize">{video.type}</span>
                            </div>
                            <div className="text-xs text-gray-400 mb-3">
                              📅 {formatDate(video.publishDate)}
                            </div>
                            <div className="flex gap-2">
                              <button 
                                className="flex-1 bg-blue-500 text-white py-2 px-3 rounded-lg hover:bg-blue-600 text-xs font-medium"
                                onClick={() => handleEditVideo(video)}
                              >
                                ✏️ Bewerken
                              </button>
                              <button 
                                className="bg-red-500 text-white py-2 px-3 rounded-lg hover:bg-red-600 text-xs font-medium"
                                onClick={() => handleDeleteVideo(video.id)}
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}


            </div>
          </div>
        </div>
      )}

      {/* Guided Video Overlay - 1/3 positioning (NOT centered) */}
      {showGuidedVideoOverlay && (
        <div 
          className="fixed inset-0 z-[10000]"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.65)',
            backdropFilter: 'blur(3px)',
            WebkitBackdropFilter: 'blur(3px)' // Safari support
          }}
          onClick={() => setShowGuidedVideoOverlay(false)}
        >
          {/* Video Container - Positioned at 1/3 from top (NOT centered) */}
          {/* CRITICAL: Using explicit positioning, NOT flex-center or align-center */}
          <div
            className="absolute"
            style={{
              left: '50%',
              transform: 'translateX(-50%)',
              // Desktop: 1/3 from top, Mobile/Tablet: slightly higher (25%) for better fit
              top: window.innerWidth < 768 ? '25%' : '33%', // 1/3 from top - NOT 50% (centered)
              width: window.innerWidth < 768 ? '95%' : '90%',
              maxWidth: '1200px',
              aspectRatio: '1900/800',
              // Mobile: ensure it fits in viewport
              maxHeight: window.innerWidth < 768 ? 'calc(75vh - 40px)' : 'calc(67vh - 40px)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative w-full h-full rounded-xl overflow-hidden bg-black">
              {/* Close Button - Top Right */}
              <button
                onClick={() => setShowGuidedVideoOverlay(false)}
                className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center text-gray-700 hover:text-gray-900 transition-colors shadow-lg"
                aria-label="Sluiten"
              >
                <span className="text-2xl font-bold leading-none">×</span>
              </button>

              {/* Video Player - NO autoplay, starts on user click */}
              <video
                src="/Guidedflow_Microvideo_Carousel.mp4"
                controls
                className="w-full h-full object-contain"
                style={{ borderRadius: '12px' }}
                preload="metadata"
              >
                Je browser ondersteunt geen video element.
              </video>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SuperKonnectedCarousel;
